import { useCallback, useEffect, useMemo } from 'react';
import { css } from '@emotion/react';
import { UsersMutations } from '@frontend/api-users';
import { useTranslation } from '@frontend/i18n';
import { formatPhoneNumber } from '@frontend/phone-numbers';
import { sentry } from '@frontend/tracking';
import { theme } from '@frontend/theme';
import { Button, ContentLoader, NakedButton, Text, TextField, useAlert, useForm } from '@frontend/design-system';
import { MFABackButton } from './mfa-back-button';
import { MFAButtonRow } from './mfa-button-row';
import { useMFAContext } from './mfa-context';

const styles = {
  field: css`
    margin: ${theme.spacing(3)} 0;
  `,
  linkButton: css`
    color: ${theme.colors.primary50};
    font-weight: ${theme.font.weight.bold};
    font-size: ${theme.font.size.large};
  `,
};

type VerifyMFAAddressProps = {
  onVerifySuccess?: () => Promise<void>;
};

export function VerifyMFAAddress({ onVerifySuccess }: VerifyMFAAddressProps) {
  const { t } = useTranslation('account');
  const alert = useAlert();
  const newVerificationMethod = useMFAContext((ctx) => ctx.newVerificationMethod);
  const nextStep = useMFAContext((ctx) => ctx.nextStep);
  const flow = useMFAContext((ctx) => ctx.flow);

  const { mutateAsync: validateContact, isLoading } = UsersMutations.useValidateContact();
  const { mutateAsync: resendContactValidation, isLoading: isResendingCode } =
    UsersMutations.useResendContactValidation();

  const verify = async (code: string) => {
    try {
      await validateContact({ code, contactId: newVerificationMethod.contactId });
      await onVerifySuccess?.();
      nextStep();
      flow.step(`verify-mfa-address`, { method: newVerificationMethod.method }).complete('success');
    } catch (error: any) {
      if (error?.status !== 403) {
        sentry.error({
          topic: 'auth',
          team: 'users-infra',
          error: `Unexpected error received during MFA verification - HTTP ${error.status} ${error}`,
        });
      }
      alert.error(
        t(
          'The verification code you entered is incorrect. Please check the code and try again. If you continue to experience issues, you can request a new verification code.'
        )
      );
      flow.step(`verify-mfa-address-failed`, { method: newVerificationMethod.method, error: error?.message });
    }
  };

  const { getFieldProps, formProps, isComplete } = useForm({
    fields: {
      code: {
        type: 'text',
        required: true,
        maxLength: 6,
      },
    },
    onSubmit(values) {
      if (!values.code) {
        return;
      }
      verify(values.code);
    },
  });
  const formRef = formProps.ref;
  const codeVal = getFieldProps('code').value;

  const autoSubmitForm = useCallback(
    (fieldVal: string) => {
      if (fieldVal.length >= 6) {
        // if the code is 6 digits long, we can submit the form automatically
        // submitting a form using formRef.current.submit() bypasses the React onSubmit handler, so this is the way
        formRef.current?.dispatchEvent(new Event('submit', { cancelable: true, bubbles: true }));
      }
    },
    [formRef]
  );

  // if the code is 6 digits long, we can submit the form automatically
  useEffect(() => {
    if (codeVal.length >= 6) {
      autoSubmitForm(codeVal);
    }
  }, [autoSubmitForm, codeVal]);

  const resendCode = useCallback(async () => {
    try {
      await resendContactValidation({ contactId: newVerificationMethod.contactId });
      flow.step(`resend-mfa-code`, { method: newVerificationMethod.method });
    } catch (error) {
      alert.error('Failed to send validation code.');
      flow.step(`resend-mfa-code-failed`, { method: newVerificationMethod.method, error: (error as Error).message });
    }
  }, [alert, newVerificationMethod.contactId, newVerificationMethod.method, resendContactValidation, flow]);

  const formattedAddress = useMemo(() => {
    if (newVerificationMethod.method === 'sms') {
      return formatPhoneNumber(newVerificationMethod?.address);
    }
    return newVerificationMethod?.address;
  }, [newVerificationMethod]);

  return (
    <>
      <ContentLoader show={isLoading || isResendingCode} />
      <form id='verify-mfa-address-form' {...formProps}>
        <Text>{t('Enter the code sent to {{address}} to complete setup.', { address: formattedAddress })}</Text>
        <div css={styles.field}>
          <TextField {...getFieldProps('code')} label={t('Verification Code')} autoFocus maxLength={6} />
        </div>
        <MFAButtonRow>
          <MFABackButton />
          <div
            css={css`
              display: flex;
              gap: ${theme.spacing(2)};
            `}
          >
            <NakedButton id='resend-mfa-code-button' css={styles.linkButton} onClick={resendCode}>
              {t('Resend')}
            </NakedButton>
            <Button type='submit' size='large' disabled={!isComplete}>
              {t('Verify')}
            </Button>
          </div>
        </MFAButtonRow>
      </form>
    </>
  );
}
